import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "../../elements/Header";
import NavigationSimple from "../../elements/NavigationSimple";
import { useFirestore } from "../../firebase/FirestoreContext";
import { collection, getDocs, getDoc, doc, addDoc } from "firebase/firestore";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import { Star, StarBorder, IosShare, Close } from "@mui/icons-material";
import ShopButton from "../../elements/ShopButton";
import { Sheet } from "react-modal-sheet";

const Reviews = () => {
  const { productID } = useParams();
  const [reviews, setReviews] = useState([]);
  const [product, setProduct] = useState(null);
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reportReason, setReportReason] = useState(null);

  const db = useFirestore();
  const environment = useContext(EnvContext);
  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;

  const fetchReviews = useCallback(async () => {
    try {
      const reviewsCollection = collection(db, `${collectionPath}Products/ProductsList/DataBase/${productID}/Reviews`);
      const reviewsSnapshot = await getDocs(reviewsCollection);
      const reviewsData = reviewsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReviews(reviewsData);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  }, [db, collectionPath, productID]);

  const fetchProduct = useCallback(async () => {
    try {
      const productDoc = await getDoc(doc(db, `${collectionPath}Listings/ListingsList/DataBase`, productID));
      if (productDoc.exists()) {
        setProduct(productDoc.data());
      }
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  }, [db, collectionPath, productID]);

  useEffect(() => {
    fetchReviews();
    fetchProduct();
  }, [fetchReviews, fetchProduct]);

  const toggleReview = (id) => {
    setExpandedReviewId((prev) => (prev === id ? null : id));
  };

  const handleReasonChange = (reason) => {
    setReportReason(reason);
  };

  const handleReportSubmit = useCallback(async () => {
    if (!reportReason) {
      alert("Please select a reason for reporting.");
      return;
    }

    try {
      const reportData = {
        reviewID: selectedReview.id,
        productID: productID,
        reason: reportReason,
        timestamp: new Date(),
      };

      const reportCollection = collection(db, `${collectionPath}Products/ProductsList/DataBase/${productID}/Reviews/${selectedReview.id}/Reports`);
      await addDoc(reportCollection, reportData);

      setReportSent(true);

      setTimeout(() => {
        setOpen(false);
        setReportSent(false);
        setReportReason(null);
      }, 2000);
    } catch (error) {
      console.error("Error submitting report:", error);
      alert("Failed to submit report. Please try again.");
    }
  }, [db, collectionPath, productID, reportReason, selectedReview]);

  const handleCloseSheet = () => {
    setOpen(false);
    setReportSent(false);
    setReportReason(null);
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>
          {environment === "staging" ? "STAGING - " : ""}Fullproof - {product?.product_name || "Reviews"}
        </title>
        <meta name="description" content={product?.product_description || "Reviews"} />
      </Helmet>

      <Header stickyHeader search={false} />
      <div style={{ height: 20 }} />
      <NavigationSimple />
      <div style={{ borderBottom: "1px solid black", marginBottom: 15 }} />

      {reviews.map((review) => (
        <div key={review.id} style={{ ...styles.reviewBox, padding: "0px 15px", border: "" }}>
          <div style={styles.name}>{review.user_uid}</div>
          <Rating value={review.rating} readOnly iconStyle={{ fontSize: "1rem" }} />
          <div style={styles.title}>{review.title}</div>
          <div style={styles.content}>
            <div style={styles.review}>
              {review.comment?.length > 200 ? (
                <>
                  {expandedReviewId === review.id ? review.comment : review.comment.slice(0, 200) + "... "}
                  <span style={styles.seeMore} onClick={() => toggleReview(review.id)}>
                    {expandedReviewId === review.id ? "Show less" : "See more"}
                  </span>
                </>
              ) : (
                review.comment
              )}
            </div>
          </div>
          <div style={styles.footer}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={styles.voteUp}>Helpful</div>
              <div style={styles.share}>
                <IosShare style={{ fontSize: "1rem", marginRight: 5 }} />
                Share
              </div>
            </div>
            <div
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedReview(review);
                setOpen(true);
              }}
            >
              Report
            </div>
          </div>
        </div>
      ))}

      {/* Report Sheet */}
      <Sheet isOpen={isOpen} onClose={handleCloseSheet} snapPoints={[0.9]} disableDrag>
        <Sheet.Container style={{ overflowY: "auto" }}>
          <Sheet.Header>
            <button onClick={handleCloseSheet} style={{ ...styles.report.closeButton, border: "1px solid red" }}>
              <Close />
            </button>
          </Sheet.Header>
          <Sheet.Content style={styles.report.container}>
            {reportSent ? (
              <div style={{ textAlign: "center" }}>
                <h3 style={{ ...styles.report.title, paddingBottom: 10 }}>Thank you!</h3>
                <p style={{ fontSize: ".8rem", paddingBottom: 10 }}>
                  We’ll check if this review meets our{" "}
                  <a href="#" style={{ textDecoration: "none" }}>
                    community guidelines
                  </a>
                  . If it doesn’t, we’ll remove it.
                </p>
                <img
                  src="/images/report.png"
                  alt="report"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 8,
                    marginTop: 8,
                  }}
                />
              </div>
            ) : (
              <>
                <h3 style={styles.report.title}>Report this review</h3>
                <p style={{ paddingBottom: 10 }}>Optional: Why are you reporting this?</p>
                <form style={styles.form}>
                  {["Off topic", "Spam", "Inappropriate", "Fake", "Other"].map((label, idx) => (
                    <div style={styles.report.inputArea} key={idx}>
                      <input type="checkbox" style={styles.report.input} checked={reportReason === label} onChange={() => handleReasonChange(label)} />
                      <div style={styles.report.inputName}>
                        <div>{label}</div>
                        <div style={{ opacity: 0.6 }}>
                          {
                            {
                              "Off topic": "Not about the product",
                              Spam: "Promotion of other products",
                              Inappropriate: "Disrespectful, hateful, obscene",
                              Fake: "Paid for, inauthentic",
                              Other: "Something else",
                            }[label]
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                  <p style={{ fontSize: ".8rem", paddingBottom: 10 }}>
                    We’ll check if this review meets our{" "}
                    <a href="#" style={{ textDecoration: "none" }}>
                      community guidelines
                    </a>
                    . If it doesn’t, we’ll remove it.
                  </p>
                  <ShopButton
                    text={"Submit"}
                    theme={"dark"}
                    containerStyles={{ width: "100%" }}
                    buttonStyle={{ flex: 1 }}
                    onClick={() => handleReportSubmit()}
                  />
                </form>
              </>
            )}
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
    </div>
  );
};

const Rating = ({ value, style, iconStyle }) => (
  <div style={{ ...styles.rating.container, ...style }}>
    {Array.from({ length: 5 }).map((_, index) => (
      <div style={styles.rating.star} key={index}>
        {index < value ? <Star style={iconStyle} /> : <StarBorder style={iconStyle} />}
      </div>
    ))}
  </div>
);

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 30,
    maxWidth: "1140px",
    margin: "8px auto"
  },
  reviewBox: {
    marginBottom: 24,
  },
  name: {
    fontSize: "0.9rem",
    fontWeight: 300,
    paddingBottom: 4,
  },
  title: {
    fontWeight: "600",
    paddingBottom: 10,
  },
  content: {
    marginTop: 8,
  },
  review: {
    fontSize: 14,
    lineHeight: 1.5,
  },
  seeMore: {
    color: "#3B72FE",
    cursor: "pointer",
    paddingLeft: 5,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 12,
  },
  voteUp: {
    padding: "6px 16px",
    border: "1px solid black",
    borderRadius: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    fontSize: 12,
  },
  share: {
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  rating: {
    container: {
      display: "flex",
      flexDirection: "row",
      paddingBottom: 5,
    },
    star: {
      marginRight: 2,
    },
  },
  report: {
    container: {
      padding: "40px 20px 20px 20px",
      fontSize: ".9rem",
    },
    title: {
      fontSize: "1rem",
      fontWeight: 600,
      paddingBottom: 3,
    },
    inputArea: {
      display: "flex",
      alignItems: "center",
      paddingBottom: 10,
    },
    input: {
      transform: "scale(.8)",
      marginTop: 3,
    },
    inputName: {
      display: "flex",
      flexDirection: "column",
      paddingLeft: 10,
    },
    closeButton: {
      position: "absolute",
      top: 10,
      right: 10,
      backgroundColor: "transparent",
      border: "none",
      fontSize: 20,
      cursor: "pointer",
      color: "black",
      padding: 10,
      zIndex: 10000,
    },
  },
};

export default Reviews;
